import React from "react";
import { CreateFunction } from "@hex-insights/app-modules";
import { Button, ClassNameProps, Heading, Modal, StyleProps } from "@hex-insights/core";
import { Form, FormState, SubmitButton, useInternalField } from "@hex-insights/forms";
import { Person, UserCreateMutation } from "../../../Utilities";
import { UserFormState, UserFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import { useCreateFormControl, useCreateFormSubmission } from "./hooks";

export type CreateControllerProps = {
	personID: Person["id"];
};

export function CreateController({ personID }: CreateControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const formStateArgs = React.useMemo(
		() => ({
			initialFormValues: { personID },
		}),
		[personID],
	);

	return (
		<React.Fragment>
			<Button variant="primary" onClick={openModal}>
				Create Account
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal} style={{ width: "fit-content", height: "30rem" }}>
					<Modal.Header>
						<Heading.H2 noMargin>Create Account</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<ControlledCreate formStateArgs={formStateArgs} formNameSuffix={personID} onSuccess={closeModal} />
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

export type ControlledCreateProps = Omit<CreateProps<UserCreateMutation["createUser"]>, "formState" | "create"> & {
	formStateArgs?: UserFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the User model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<UserFormValues.Create>;
	create: CreateFunction<UserFormValues.Create, T>;
	onSuccess: (formState: FormState<UserFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<UserFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the User model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "user.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Create Account
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<UserFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	useInternalField(formState, "timeZoneName");
	useInternalField(formState, "personID");

	return (
		<React.Fragment>
			<Field.Email formState={formState} />
			<Field.PermissionGroups.Select formState={formState} />
		</React.Fragment>
	);
}
